import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faEnvelope, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import wedlLogo from '../../assets/images/wedl_logo.svg';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!email) {
        throw new Error('E-Mail-Adresse ist erforderlich');
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      }

      await onSubmit(email);
      setIsSuccess(true);

    } catch (error: any) {
      setError(error.message || 'Fehler beim Senden der Reset-E-Mail');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setIsSuccess(false);
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>

              <div className="p-8">
                {isSuccess ? (
                  /* Success State */
                  <div className="text-center">
                    <img 
                      src={wedlLogo} 
                      alt="WEDL Logo" 
                      className="mx-auto mb-6 h-12 w-auto"
                    />
                    <div className="text-6xl mb-4">📧</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      E-Mail versendet!
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Wir haben eine E-Mail mit einem temporären Passwort an <strong>{email}</strong> gesendet.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <strong>Wichtig:</strong> Überprüfen Sie auch Ihren Spam-Ordner, falls Sie keine E-Mail erhalten.
                      </p>
                    </div>
                    <button
                      onClick={handleClose}
                      className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-medium shadow-lg transition-all duration-200"
                    >
                      Schließen
                    </button>
                  </div>
                ) : (
                  /* Form State */
                  <>
                    {/* Header */}
                    <div className="text-center mb-8">
                      <img 
                        src={wedlLogo} 
                        alt="WEDL Logo" 
                        className="mx-auto mb-6 h-12 w-auto"
                      />
                      <div className="flex items-center justify-center mb-4">
                        <FontAwesomeIcon icon={faKey} className="text-red-600 text-2xl mr-3" />
                        <h2 className="text-2xl font-bold text-gray-900">
                          Passwort zurücksetzen
                        </h2>
                      </div>
                      <p className="text-gray-600">
                        Wir senden Ihnen eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts.
                      </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Email Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                          E-Mail-Adresse
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          placeholder="ihre@email.com"
                          required
                          autoFocus
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Das temporäre Passwort wird an diese E-Mail-Adresse gesendet.
                        </p>
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-800 text-sm font-medium">{error}</p>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="space-y-3">
                        {/* Submit Button */}
                        <button
                          type="submit"
                          disabled={isLoading || !email}
                          className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium shadow-lg transition-all duration-200"
                        >
                          {isLoading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                              Wird gesendet...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                              Reset-E-Mail senden
                            </>
                          )}
                        </button>

                        {/* Cancel Button */}
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={isLoading}
                          className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200"
                        >
                          Abbrechen
                        </button>
                      </div>
                    </form>

                    {/* Info Notice */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-900 mb-2">Information:</h3>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>• Sie erhalten ein temporäres Passwort per E-Mail</li>
                        <li>• Das temporäre Passwort ist 24 Stunden gültig</li>
                        <li>• Sie müssen beim ersten Login ein neues Passwort erstellen</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default ForgotPasswordModal;
